import React, { useState, useRef, useEffect } from 'react';
import { Message } from '../../types';
import { formatMessageTime } from '../../utils/dateUtils';
import { EllipsisVerticalIcon, FlagIcon } from '@heroicons/react/24/outline';

interface MessageBubbleProps {
  message: Message;
  isOwnMessage: boolean;
  onReportMessage?: (message: Message) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwnMessage,
  onReportMessage
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const handleReport = () => {
    setMenuOpen(false);
    if (onReportMessage) {
      onReportMessage(message);
    }
  };

  return (
    <div
      className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'} mb-3 md:mb-4 group px-2 md:px-0`}
    >
      <div
        className={`flex items-center gap-1 md:gap-2 ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
      >
        {/* Message Bubble */}
        <div
          className={`
            max-w-[85%] sm:max-w-[75%] md:max-w-[70%] rounded-2xl px-3 py-2 md:px-4 md:py-2.5 relative shadow-sm
            ${
              isOwnMessage
                ? 'bg-blue-600 text-white rounded-tr-none'
                : 'bg-white text-gray-800 border border-gray-100 rounded-tl-none'
            }
          `}
        >
          {message.type === 'image' && message.metadata?.fileUrl ? (
            <img
              src={message.metadata.fileUrl}
              alt="Sent attachment"
              className="rounded-lg mb-2 max-h-60 object-cover w-full cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(message.metadata?.fileUrl, '_blank')}
            />
          ) : (
            <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
              {message.content}
            </p>
          )}

          <div
            className={`text-[10px] mt-1 text-right ${isOwnMessage ? 'text-blue-100' : 'text-gray-400'}`}
          >
            {formatMessageTime(message.timestamp)}
            {isOwnMessage && (
              <span className="ml-1">
                {message.status === 'read'
                  ? '✓✓'
                  : message.status === 'delivered'
                    ? '✓✓'
                    : '✓'}
              </span>
            )}
          </div>
        </div>

        {/* 3-dot Menu - always visible but more prominent on hover */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 md:p-1.5 text-gray-300 hover:text-gray-600 active:text-gray-700 transition-all rounded-full hover:bg-gray-100 active:bg-gray-200 group-hover:text-gray-500 touch-manipulation"
            title="More options"
          >
            <EllipsisVerticalIcon className="w-5 h-5 md:w-4 md:h-4" />
          </button>

          {/* Dropdown Menu */}
          {menuOpen && (
            <div
              className={`absolute z-20 mt-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 py-1 ${
                isOwnMessage ? 'right-0' : 'left-0'
              }`}
            >
              {!isOwnMessage && onReportMessage && (
                <button
                  onClick={handleReport}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <FlagIcon className="w-4 h-4" />
                  Report
                </button>
              )}
              {/* Placeholder for other menu items (copy, delete own, etc.) */}
              {isOwnMessage && (
                <div className="px-4 py-2 text-sm text-gray-400 italic">
                  No actions available
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
